/**
 * Whether a new message belongs in the open thread. The side panel reopens
 * the last thread, so a question asked hours later (or about something else)
 * would land in an old conversation. Two cheap checks, no model call:
 *
 * - stale: nothing has happened in the thread for {@link STALE_AFTER_MS};
 * - off topic: the message shares no content words with the thread's title
 *   and last preview.
 *
 * Both are advisory: the panel asks, the attorney decides.
 */
import type { AgentThread } from "./api/agentApi";

export const STALE_AFTER_MS = 4 * 60 * 60 * 1000;

/** Words too common to say anything about a topic. */
const STOP = new Set(
  (
    "the and for with that this from what when where which who whom whose why how " +
    "are was were been being have has had does did doing can could should would will " +
    "shall may might must not but you your yours our ours their them they she her his " +
    "him its it's i'm i've let lets please thanks thank just also any all some more most " +
    "about into onto over under than then there here these those such only very really " +
    "make made get got give gave take took need needs want wants like know see look " +
    "tell show find help draft write check review update new today now case client " +
    "casey thread chat message ok okay yes yeah one two"
  ).split(" ")
);

/** The content words of `text`: lowercased, 3+ letters, minus stop words. */
export const contentWords = (text: string | null | undefined): Set<string> => {
  const words = (text ?? "")
    .toLowerCase()
    .replace(/@\S+/g, " ")
    .match(/[a-z0-9][a-z0-9'-]{2,}/g);
  return new Set((words ?? []).filter((w) => !STOP.has(w)));
};

/** Epoch ms of the thread's last activity (its newest message, else its update). */
export const lastActivityMs = (thread: AgentThread): number | null => {
  if (typeof thread.last_message_ns === "number" && thread.last_message_ns > 0)
    return Math.floor(thread.last_message_ns / 1e6);
  const at = Date.parse(thread.updated_at ?? thread.created_at ?? "");
  return Number.isNaN(at) ? null : at;
};

export const isStale = (
  thread: AgentThread,
  now: number = Date.now()
): boolean => {
  const at = lastActivityMs(thread);
  return at !== null && now - at > STALE_AFTER_MS;
};

/**
 * The message is about something else: it has at least three content words
 * and none of them appear in the thread's title or last preview.
 */
export const isOffTopic = (thread: AgentThread, text: string): boolean => {
  const said = contentWords(text);
  if (said.size < 3) return false;
  const topic = contentWords(
    `${thread.title ?? ""} ${thread.last_message_preview ?? ""}`
  );
  if (!topic.size) return false;
  for (const w of said) if (topic.has(w)) return false;
  return true;
};

export type ContinuityReason = "stale" | "off_topic";

/** Why to suggest a new thread for `text`, or null to just send it. */
export const continuityCheck = (
  thread: AgentThread | null | undefined,
  text: string,
  now: number = Date.now()
): ContinuityReason | null => {
  if (!thread || !thread.message_count) return null;
  if (thread.kind === "channel" || thread.kind === "updates") return null;
  if (isStale(thread, now)) return "stale";
  if (isOffTopic(thread, text)) return "off_topic";
  return null;
};
